import { useEffect, useRef } from "react";
import type { Step } from "../App";
import { useI18n } from "../i18n";
import { techniqueLevel, explainStep } from "@sudoku/shared";
import "./StepsPanel.css";

interface StepsPanelProps {
  steps: Step[];
  onStepHover?: (index: number | null) => void;
  activeStepIndex?: number;
  onStepClick?: (index: number) => void;
  mode?: "default" | "stepping";
}

// Renkler dil-bağımsız (saf görsel), i18n gerekmiyor.
const techniqueColor: Record<string, string> = {
  "Naked Single": "#16a34a",
  "Hidden Single": "#2563eb",
  "Naked Pair": "#e11d48",
  "Naked Triple": "#be123c",
  "Naked Quad": "#9f1239",
  "Hidden Pair": "#c026d3",
  "Hidden Triple": "#a21caf",
  "Hidden Quad": "#86198f",
  "Pointing Pairs": "#ea580c",
  "Box/Line Reduction": "#d97706",
  "X-Wing": "#dc2626",
  "Swordfish": "#b91c1c",
  "Jellyfish": "#991b1b",
  "Finned X-Wing": "#c81e1e",
  "Finned Swordfish": "#a31818",
  "XY-Wing": "#7c2d12",
  "XYZ-Wing": "#6c2710",
  "Simple Colouring": "#0d9488",
  "X-Cycles": "#0f766e",
  "XY-Chain": "#115e59",
  "Skyscraper": "#6366f1",
  "2-String Kite": "#4f46e5",
  "Empty Rectangle": "#4338ca",
  "W-Wing": "#7e22ce",
  "BUG+1": "#a855f7",
  "Unique Rectangle": "#4f46e5",
  "Bowman's Bingo": "#78350f",
};

// Zorluk rengi seviyeye göre (1-5). Etiket TEK KAYNAKTAN türer:
// techniqueLevel(name) → oyun seviyesi → i18n difficulties[level].
const difficultyLevelColor: Record<number, string> = {
  1: "#16a34a", 2: "#0891b2", 3: "#d97706", 4: "#dc2626", 5: "#7c3aed",
};

function isElimination(step: Step): boolean {
  return step.value === 0 && step.row === -1;
}

/** A1 gibi hücre referanslarını okunabilir etiketlere dönüştürür */
function renderReason(reason: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const regex = /([A-HJ])([1-9])/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(reason)) !== null) {
    if (match.index > lastIndex) {
      parts.push(reason.slice(lastIndex, match.index));
    }
    const rowLetter = match[1];
    const colNum = match[2];
    parts.push(
      <span
        key={key++}
        className="cell-ref"
        title={`Row ${rowLetter}, Col ${colNum}`}
      >
        {rowLetter}{colNum}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < reason.length) {
    parts.push(reason.slice(lastIndex));
  }
  return parts.length > 0 ? parts : reason;
}

export function StepsPanel({ steps, onStepHover, activeStepIndex, onStepClick, mode = "default" }: StepsPanelProps) {
  const { t, locale } = useI18n();
  const sp = t.stepsPanel as any;
  const placementCount = steps.filter((s) => !isElimination(s)).length;
  const eliminationCount = steps.filter((s) => isElimination(s)).length;
  const listRef = useRef<HTMLUListElement>(null);
  const activeRef = useRef<HTMLLIElement>(null);

  // Aktif adıma otomatik kaydır
  useEffect(() => {
    if (mode === "stepping" && activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeStepIndex, mode]);

  const isStepping = mode === "stepping";

  return (
    <div className="steps-panel">
      <h2 className="steps-title">{sp.title}</h2>
      {steps.length === 0 ? (
        <p className="steps-empty">{sp.emptyState}</p>
      ) : (
        <>
          <p className="steps-count">
            {placementCount} {sp.placementsLabel}
            {eliminationCount > 0 && `, ${eliminationCount} ${sp.eliminationsLabel}`}
            {" — "}{sp.totalStepsLabel.replace('{n}', String(steps.length))}
          </p>
          <ul className="steps-list" ref={listRef}>
            {steps.map((step, i) => {
              const elim = isElimination(step);
              const isActive = isStepping && activeStepIndex === i;
              const isPast = isStepping && activeStepIndex !== undefined && i <= activeStepIndex;
              const isFuture = isStepping && (activeStepIndex === undefined || i > activeStepIndex);
              const diffLevel = techniqueLevel(step.technique);
              const diffLabel = (t.difficulties as Record<string, string>)[String(diffLevel)];
              return (
                <li
                  key={i}
                  ref={isActive ? activeRef : undefined}
                  className={[
                    "steps-item",
                    elim ? "steps-item--elim" : "",
                    isActive ? "steps-item--active" : "",
                    isFuture ? "steps-item--future" : "",
                    isPast && !isActive ? "steps-item--past" : "",
                  ].filter(Boolean).join(" ")}
                  onMouseEnter={() => onStepHover?.(i)}
                  onMouseLeave={() => onStepHover?.(null)}
                  onClick={() => isStepping && onStepClick?.(i)}
                >
                  <div className="step-header">
                    <span className="step-number">{i + 1}</span>
                    <span
                      className="step-technique"
                      style={{
                        color: techniqueColor[step.technique] ?? "#64748b",
                      }}
                      data-tooltip={sp.descriptions[step.technique] ?? ""}
                    >
                      {step.technique}
                    </span>
                    {diffLabel && (
                      <span
                        className="step-difficulty"
                        style={{
                          color: difficultyLevelColor[diffLevel],
                          borderColor: difficultyLevelColor[diffLevel],
                        }}
                      >
                        {diffLabel}
                      </span>
                    )}
                    {!elim && (
                      <span className="step-value">{step.value}</span>
                    )}
                    {elim && (
                      <span className="step-badge-elim">{sp.elimBadge}</span>
                    )}
                  </div>
                  <p className="step-reason">{renderReason(explainStep(step, locale))}</p>
                  {sp.notes[step.technique] && (
                    <p className="step-note">{sp.notes[step.technique]}</p>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
